import { Alert, AlertTitle } from '@material-ui/lab';
import { omitBy } from 'lodash';
import { ReactElement, ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useBidExceedsBuyPrice } from '../../hooks/useBidExceedsBuyPrice';
import { BidBuyErrors } from '../../modules/bid-buy-helpers';
import { DetailCarDTO } from '../../modules/generated/api';
import { countryCodeToRegion } from '../../modules/labels';
import { formatUnitValue } from '../../modules/unit-helpers';
import { getAccountValuationCountry } from '../../modules/valuation-country-helpers';
import i18n from '../../setup/i18n';

type BidAlertsProps = {
  className?: string;
  car: DetailCarDTO;
  errors: BidBuyErrors;
  supportsBidding: boolean;
  supportsBuying: boolean;
};

export const BidAlerts = ({
  className,
  car,
  errors,
  supportsBidding,
  supportsBuying,
}: BidAlertsProps): ReactElement => {
  const onlyBuying = !supportsBidding && supportsBuying;
  const { t } = useTranslation();
  const bidExceedsBuyPrice = useBidExceedsBuyPrice(car.potential?.base?.amount, supportsBidding, supportsBuying);

  // Warnings normally do not lock submit
  const warnings = omitBy(
    {
      colleagueBidActive: !onlyBuying && !!car.bidStateInfo?.highestHistoricBid?.amount,
      bidMustBeSmallerThanBuyPrice: bidExceedsBuyPrice, // this one does on purpose
    },
    (pred) => !pred,
  );

  const messages: Record<keyof BidBuyErrors, ReactNode> = {
    colleagueBidActive: (
      <Trans
        t={t}
        i18nKey="alerts.prevBid"
        values={{
          minBid: formatUnitValue((car.bidStateInfo?.highestHistoricBid?.amount || 0) + 1, '€'),
        }}
      />
    ),
    credentialsMissing: t('alerts.missingCred'),
    illegalAccountValuationCountry: t('bids.functionBuyNotAvailable', {
      country: countryCodeToRegion(getAccountValuationCountry(), i18n.language).label,
    }),
    bidMustBeSmallerThanBuyPrice: (
      <Trans
        t={t}
        i18nKey="alerts.bidMustBeSmallerThanBuyPrice"
        values={{
          buyPrice: formatUnitValue(car.potential?.base?.amount || 0, '€'),
        }}
      />
    ),
  };

  return (
    <>
      {/* Errors */}
      {Object.keys(errors).map((key) => (
        <Alert key={key} severity="error" className={className}>
          <AlertTitle>{t('bids.status.error')}</AlertTitle>
          {messages[key as keyof BidBuyErrors]}
        </Alert>
      ))}
      {/* Warnings */}
      {Object.keys(warnings).map((key) => (
        <Alert key={key} severity="warning" className={className}>
          <AlertTitle>{t('alerts.confirmBidTitle')}</AlertTitle>
          {messages[key as keyof BidBuyErrors]}
        </Alert>
      ))}
    </>
  );
};
